/**
 * TestCalls - the called tests on this screen.
 *
 * - `TestPrompts`: for the GM, each call still waiting, person by person:
 *   whether they have seen it, Roll for them, and Cancel for the whole
 *   call. (The prompt a PLAYER sees is a row of the asks tray since
 *   v9.5.3: `components/asks/rows.jsx`.)
 * - `CallTestButton`: the word that opens the dialog, for whoever directs.
 *
 * Both draw nothing when there is nothing to draw.
 */
import { useState } from 'react';
import common from '../../styles/common.module.css';
import styles from '../../styles/CallForTest.module.css';
import CallForTestDialog from './CallForTestDialog';
import { readTestAsk, callLabel } from '../../engine/narrative/testAsk';
import { TEST_ASK } from '../../hooks/useTestCalls';
/** One person of an open call, on the GM's screen. */
function WaitingOn({ open, combatantId, name, seen, testCalls }) {
  return (
    <div className={styles.callLine} data-testid={`call-waiting-${combatantId}`}>
      <span>
        Waiting on {name} for {callLabel(open.call)}
        {seen ? ' (seen)' : ''}
      </span>
      <button
        type="button"
        className={common.btnSmall}
        data-testid={`call-roll-for-${combatantId}`}
        onClick={() => testCalls.rollFor(open.id, combatantId)}
      >
        Roll for them
      </button>
    </div>
  );
}

/** Has this person's prompt been seen? Read off the asks this seat is waiting on. */
function seenBy(asks, callId, combatantId) {
  return asks.outgoing.some((a) => {
    const prompt = a.kind === TEST_ASK ? readTestAsk(a.payload) : null;
    return prompt?.callId === callId && prompt.combatantId === combatantId && a.status === 'seen';
  });
}

function OpenCall({ open, roster, asks, testCalls }) {
  const nameOf = (id) => roster.find((c) => c.id === id)?.name ?? id;
  return (
    <>
      {open.waiting.map((id) => (
        <WaitingOn
          key={id}
          open={open}
          combatantId={id}
          name={nameOf(id)}
          seen={seenBy(asks, open.id, id)}
          testCalls={testCalls}
        />
      ))}
      <div className={styles.callLine}>
        <span />
        <button
          type="button"
          className={common.btnSmall}
          data-testid="call-cancel"
          onClick={() => testCalls.cancelCall(open.id)}
        >
          Cancel the call
        </button>
      </div>
    </>
  );
}

/**
 * @param {object} props
 * @param {import('../../hooks/useAsks').Asks} props.asks
 * @param {{openCalls: object[], rollFor: Function, cancelCall: Function}} props.testCalls
 * @param {{id: string, name: string}[]} props.roster
 */
export function TestPrompts({ asks, testCalls, roster }) {
  if (testCalls.openCalls.length === 0) return null;
  return (
    <div className={styles.calls} role="status" data-testid="test-calls">
      {testCalls.openCalls.map((open) => (
        <OpenCall key={open.id} open={open} roster={roster} asks={asks} testCalls={testCalls} />
      ))}
    </div>
  );
}

/**
 * @param {object} props
 * @param {{id: string, name: string}[]} props.roster
 * @param {string[]} props.party - the party's combatant ids
 * @param {(call: object) => void} props.onCall
 * @param {object[]} [props.clocks], [props.entries], {number} [props.assistBonus] - handed on to the dialog
 */
export function CallTestButton({
  roster,
  party,
  onCall,
  clocks,
  entries,
  assistBonus,
  people,
  actedBy,
  actingBy,
  gmUid,
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={common.btnSmall} data-testid="call-test" onClick={() => setOpen(true)}>
        Call a test
      </button>
      {open && (
        <CallForTestDialog
          roster={roster}
          party={party}
          clocks={clocks}
          entries={entries}
          assistBonus={assistBonus}
          people={people}
          actedBy={actedBy}
          actingBy={actingBy}
          gmUid={gmUid}
          onCall={onCall}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
